/**
 * API Key Validation Utility
 * Provides functions to validate API key availability for different providers
 */

/**
 * True when the account should use platform/root provider keys instead of BYOK.
 * Checks user flag, plan flag, admin role, and root-agent mode.
 */
export const usesRootCredentials = ({
  user,
  subscription,
  isRootAgent = false,
} = {}) => {
  if (isRootAgent) return true;
  const role = user?.role;
  if (role === 'admin' || role === 'super-admin') return true;
  if (user?.useRootCredentials === true || user?.use_root_credentials === true) {
    return true;
  }
  const plan = subscription?.plan || subscription;
  const planName = String(
    plan?.name || subscription?.planName || plan?.planName || ''
  ).trim();
  // BYOK plans always require the user's own keys, even if a plan row was
  // incorrectly flagged with use_root_credentials=true.
  if (/^BYOK$/i.test(planName)) {
    return false;
  }
  if (plan?.useRootCredentials === true || plan?.use_root_credentials === true) {
    return true;
  }
  return false;
};

/**
 * Validates if an API key is available for a given provider
 * @param {string} provider - The provider name (e.g., 'openai', 'fireworks', 'deepgram')
 * @param {Object} apiKeys - The user's API keys object
 * @returns {Object} - { isValid: boolean, message: string, hasKey: boolean }
 */
export const validateApiKey = (provider, apiKeys = {}) => {
  if (!provider) {
    return {
      isValid: false,
      message: 'No provider selected',
      hasKey: false,
    };
  }

  const providerData = apiKeys[provider];
  const hasKey = providerData?.hasKey && providerData?.enabled;

  if (!hasKey) {
    return {
      isValid: false,
      message: `API key for ${provider} is not configured. Please add your API key in Settings.`,
      hasKey: false,
    };
  }

  return {
    isValid: true,
    message: `${provider} API key is configured and ready`,
    hasKey: true,
  };
};

/**
 * Validates multiple providers at once
 * @param {Object} providers - Object with provider names as keys
 * @param {Object} apiKeys - The user's API keys object
 * @returns {Object} - Object with validation results for each provider
 */
export const validateMultipleApiKeys = (providers, apiKeys = {}) => {
  const results = {};

  Object.entries(providers).forEach(([key, provider]) => {
    if (provider && provider.trim()) {
      results[key] = validateApiKey(provider, apiKeys);
    }
  });

  return results;
};

/**
 * Gets validation summary for agent configuration
 * @param {Object} agentConfig - Agent configuration object
 * @param {Object} apiKeys - The user's API keys object
 * @returns {Object} - { isValid: boolean, errors: Array, warnings: Array }
 */
export const validateAgentConfiguration = (agentConfig, apiKeys = {}) => {
  const errors = [];
  const warnings = [];
  let isValid = true;

  // Check AI provider
  if (agentConfig.aiProvider) {
    const aiValidation = validateApiKey(agentConfig.aiProvider, apiKeys);
    if (!aiValidation.isValid) {
      errors.push(`AI Provider (${agentConfig.aiProvider}): ${aiValidation.message}`);
      isValid = false;
    }
  }

  // Check TTS provider
  if (agentConfig.voiceProvider || agentConfig.ttsProvider) {
    const ttsProvider = agentConfig.voiceProvider || agentConfig.ttsProvider;
    const ttsValidation = validateApiKey(ttsProvider, apiKeys);
    if (!ttsValidation.isValid) {
      errors.push(`TTS Provider (${ttsProvider}): ${ttsValidation.message}`);
      isValid = false;
    }
  }

  // Check STT provider
  if (agentConfig.sttProvider) {
    const sttValidation = validateApiKey(agentConfig.sttProvider, apiKeys);
    if (!sttValidation.isValid) {
      errors.push(`STT Provider (${agentConfig.sttProvider}): ${sttValidation.message}`);
      isValid = false;
    }
  }

  return {
    isValid,
    errors,
    warnings,
  };
};

/**
 * Gets validation summary for test configuration
 * @param {Object} testConfig - Test configuration object
 * @param {Object} apiKeys - The user's API keys object
 * @returns {Object} - { isValid: boolean, errors: Array, warnings: Array }
 */
export const validateTestConfiguration = (testConfig, apiKeys = {}) => {
  const errors = [];
  const warnings = [];
  let isValid = true;

  // Check brain provider
  if (testConfig.brain?.provider) {
    const brainValidation = validateApiKey(testConfig.brain.provider, apiKeys);
    if (!brainValidation.isValid) {
      errors.push(`Brain Provider (${testConfig.brain.provider}): ${brainValidation.message}`);
      isValid = false;
    }
  }

  // Check TTS provider
  if (testConfig.tts?.provider) {
    const ttsValidation = validateApiKey(testConfig.tts.provider, apiKeys);
    if (!ttsValidation.isValid) {
      errors.push(`TTS Provider (${testConfig.tts.provider}): ${ttsValidation.message}`);
      isValid = false;
    }
  }

  // Check STT provider
  if (testConfig.stt?.provider) {
    const sttValidation = validateApiKey(testConfig.stt.provider, apiKeys);
    if (!sttValidation.isValid) {
      errors.push(`STT Provider (${testConfig.stt.provider}): ${sttValidation.message}`);
      isValid = false;
    }
  }

  return {
    isValid,
    errors,
    warnings,
  };
};

/**
 * Creates a user-friendly error message for API key validation
 * @param {Array} errors - Array of error messages
 * @returns {string} - Formatted error message
 */
export const formatApiKeyErrors = errors => {
  if (errors.length === 0) return '';

  if (errors.length === 1) {
    return errors[0];
  }

  return `Multiple API key issues found:\n${errors.map((error, index) => `${index + 1}. ${error}`).join('\n')}`;
};

/**
 * Gets the settings URL for API key management
 * @returns {string} - URL to the API keys settings page
 */
export const getApiKeysSettingsUrl = () => {
  return '/settings?tab=api-keys';
};
